import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CreditCard, ShieldCheck, ArrowLeft, ClipboardList } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorState from '../../components/ErrorState';
import FormInput from '../../components/FormInput';
import EmailPromptModal from '../../components/EmailPromptModal';
import { errorMessage } from '../../api/client';
import * as pkgApi from '../../api/packages.api';
import * as ordersApi from '../../api/orders.api';
import * as paymentsApi from '../../api/payments.api';
import { useAuth } from '../../hooks/useAuth';
import { formatMoney } from '../../utils/format';
import { trackBeginCheckout } from '../../utils/analytics';

function Checkout() {
  const { packageId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState('');
  const [pendingOrder, setPendingOrder] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  useEffect(() => {
    pkgApi.getPackage(packageId)
      .then(setPkg)
      .catch((e) => setErr(errorMessage(e)))
      .finally(() => setLoading(false));
  }, [packageId]);

  // Extracted so we can call it either from onPlaceAndPay (email already
  // present) or after the email-prompt modal saves a fresh address.
  async function startPayment(order, customerEmail) {
    try {
      const pay = await paymentsApi.createPayment(order._id, customerEmail);
      if (pay.paymentUrl) {
        window.location.href = pay.paymentUrl;
        return;
      }
      toast.success('Order created');
      navigate(`/orders/${order._id}`);
    } catch (payErr) {
      // Order succeeded but payment couldn't be initialised — still navigate to order.
      toast.error(`Order created but payment failed: ${errorMessage(payErr)}`);
      navigate(`/orders/${order._id}`);
    }
  }

  async function onPlaceAndPay() {
    setSubmitting(true);
    try {
      const order = await ordersApi.createOrder(packageId, notes);
      // GA4 conversion event — customer has clicked "Place order & pay".
      trackBeginCheckout({
        value: pkg.price || 1599.00,
        currency: pkg.currency || 'MYR',
        order_id: order._id,
        package_code: pkg.code || pkg.name,
      });
      // Curlec requires customer.email — if the customer signed up with
      // only a phone, pause here to capture one before creating the link.
      if (!user?.email) {
        setPendingOrder(order);
        setEmailModalOpen(true);
        return; // resume in onEmailSaved
      }
      await startPayment(order);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function onEmailSaved(email) {
    // Client 2026-09-08: email is per-payment only, not saved to profile.
    setEmailModalOpen(false);
    if (pendingOrder) await startPayment(pendingOrder, email);
  }

  function onEmailCancel() {
    setEmailModalOpen(false);
    if (pendingOrder?._id) {
      toast('You can pay from your order once an email is added.');
      navigate(`/orders/${pendingOrder._id}`, { replace: true });
    }
    setPendingOrder(null);
  }

  if (loading) return <LoadingSpinner size="lg" />;
  if (err || !pkg) return <ErrorState message={err || 'Package not found'} action={<Link to="/packages" className="btn"><ArrowLeft size={14} /> Back</Link>} />;

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Packages', to: '/packages' },
          { label: pkg.name, to: `/packages/${pkg._id}` },
          { label: 'Checkout' }
        ]}
        title="Checkout"
        subtitle="Review your booking and proceed to payment."
      />

      <div className="grid grid-2">
        <div className="card">
          <div className="card-title"><h3>Booking summary</h3></div>
          <h3 style={{ fontSize: 18, marginBottom: 4 }}>{pkg.name}</h3>
          <div className="muted text-sm" style={{ marginBottom: 12 }}>{pkg.description || 'Comprehensive screening package.'}</div>

          {pkg.tests?.length > 0 && (
            <div className="col" style={{ gap: 0, marginBottom: 12 }}>
              {pkg.tests.map((t, i) => (
                <div key={i} className="list-row" style={{ padding: '8px 0' }}>
                  <div className="row" style={{ gap: 10 }}>
                    <ClipboardList size={14} color="var(--brand-700)" />
                    <span className="text-sm">{t}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <FormInput
            label="Notes for the lab (optional)"
            textarea
            rows={3}
            placeholder="e.g. Prefer morning slot, fasting since 8pm…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="card" style={{ alignSelf: 'start' }}>
          <div className="card-title"><h3>Payment</h3></div>
          <dl className="kvs" style={{ gridTemplateColumns: '1fr auto' }}>
            <dt>User</dt><dd>{user?.name}</dd>
            <dt>Email</dt>
            <dd>{user?.email || <span className="muted">— we'll ask before payment</span>}</dd>
            <dt>Package price</dt><dd>{formatMoney(pkg.price, pkg.currency || 'MYR')}</dd>
          </dl>
          <div className="divider" />
          <div className="row between" style={{ marginBottom: 'var(--s-4)' }}>
            <span className="bold">Total payable</span>
            <span className="bold text-2xl">{formatMoney(pkg.price, pkg.currency || 'MYR')}</span>
          </div>

          <button className="btn btn-primary btn-block" onClick={onPlaceAndPay} disabled={submitting}>
            {submitting ? <span className="spinner" /> : <CreditCard size={16} />}
            Continue to Payment
          </button>

          <div className="row" style={{ gap: 8, marginTop: 12, color: 'var(--text-muted)', fontSize: 12 }}>
            <ShieldCheck size={14} />
            <span>Secure checkout · FPX, cards, e-wallets via Curlec</span>
          </div>
        </div>
      </div>

      <EmailPromptModal
        open={emailModalOpen}
        busy={submitting}
        onSuccess={onEmailSaved}
        onCancel={onEmailCancel}
      />
    </>
  );
}

export default Checkout;
